import type { IBillingProfile, IBillingProfileInput } from 'src/pages/tenants/tenants.types';

import { z as zod } from 'zod';
import { isValidPhoneNumber } from 'react-phone-number-input/input';

// ----------------------------------------------------------------------

export type BillingProfileFormValues = {
  name: string;
  nameEn: string;
  vatNumber: string;
  mobile: string;
  street: string;
  buildingNumber: string;
  plotIdentification: string;
  neighborhood: string;
  city: string;
  zip: string;
  countryCode: string;
  stateCode: string;
};

type FieldKey = keyof BillingProfileFormValues;

/**
 * Fields that must be filled before a new purchase.
 * PM to confirm: VAT for every customer, and whether plotIdentification is required.
 */
export const BILLING_PROFILE_REQUIRED_FIELDS: FieldKey[] = [
  'name',
  'nameEn',
  'vatNumber',
  'street',
  'buildingNumber',
  'neighborhood',
  'city',
  'zip',
  'countryCode',
  'stateCode',
];

/** Backend column limits; longer values fail with a server error. */
const MAX_LENGTH: Record<FieldKey, number> = {
  name: 255,
  nameEn: 255,
  vatNumber: 15,
  mobile: 20,
  street: 255,
  buildingNumber: 10,
  plotIdentification: 10,
  neighborhood: 255,
  city: 100,
  zip: 10,
  countryCode: 2,
  stateCode: 10,
};

/** Saudi national-address formats; only enforced when the country is SA. */
const SA_FORMATS: Partial<Record<FieldKey, { pattern: RegExp; message: string }>> = {
  vatNumber: { pattern: /^3\d{13}3$/, message: 'BILLING_PROFILE_VAT_INVALID' },
  buildingNumber: { pattern: /^\d{4}$/, message: 'BILLING_PROFILE_BUILDING_NUMBER_INVALID' },
  plotIdentification: { pattern: /^\d{4}$/, message: 'BILLING_PROFILE_PLOT_ID_INVALID' },
  zip: { pattern: /^\d{5}$/, message: 'BILLING_PROFILE_ZIP_INVALID' },
};

/**
 * Arabic company name: Arabic script (incl. Arabic punctuation and digits), Western digits,
 * spaces and common company-name punctuation. Latin letters are rejected.
 */
const ARABIC_NAME_PATTERN =
  /^[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF0-9\s.,&()/'"-]+$/;
/** At least one Arabic letter, so a name of only digits or punctuation is rejected. */
const ARABIC_LETTER_PATTERN = /[\u0621-\u064A]/;

/** ISO 3166-2:SA region codes. */
export const SA_REGIONS = [
  { code: '01', label: 'BILLING_PROFILE_REGION_RIYADH' },
  { code: '02', label: 'BILLING_PROFILE_REGION_MAKKAH' },
  { code: '03', label: 'BILLING_PROFILE_REGION_MADINAH' },
  { code: '04', label: 'BILLING_PROFILE_REGION_EASTERN' },
  { code: '05', label: 'BILLING_PROFILE_REGION_QASSIM' },
  { code: '06', label: 'BILLING_PROFILE_REGION_HAIL' },
  { code: '07', label: 'BILLING_PROFILE_REGION_TABUK' },
  { code: '08', label: 'BILLING_PROFILE_REGION_NORTHERN_BORDERS' },
  { code: '09', label: 'BILLING_PROFILE_REGION_JAZAN' },
  { code: '10', label: 'BILLING_PROFILE_REGION_NAJRAN' },
  { code: '11', label: 'BILLING_PROFILE_REGION_BAHAH' },
  { code: '12', label: 'BILLING_PROFILE_REGION_JAWF' },
  { code: '14', label: 'BILLING_PROFILE_REGION_ASIR' },
];

export const DEFAULT_COUNTRY_CODE = 'SA';

// ----------------------------------------------------------------------

export function getMissingBillingProfileFields(profile: IBillingProfile | null | undefined) {
  return BILLING_PROFILE_REQUIRED_FIELDS.filter((key) => !profile?.[key]?.toString().trim());
}

export function toBillingProfileFormValues(
  profile: IBillingProfile | null | undefined
): BillingProfileFormValues {
  return {
    name: profile?.name ?? '',
    nameEn: profile?.nameEn ?? '',
    vatNumber: profile?.vatNumber ?? '',
    mobile: profile?.mobile ?? '',
    street: profile?.street ?? '',
    buildingNumber: profile?.buildingNumber ?? '',
    plotIdentification: profile?.plotIdentification ?? '',
    neighborhood: profile?.neighborhood ?? '',
    city: profile?.city ?? '',
    zip: profile?.zip ?? '',
    countryCode: profile?.countryCode ?? DEFAULT_COUNTRY_CODE,
    stateCode: profile?.stateCode ?? '',
  };
}

/** Empty values are sent as null so the backend clears them. */
export function toBillingProfilePayload(values: BillingProfileFormValues): IBillingProfileInput {
  const payload: IBillingProfileInput = { isCompany: true };
  (Object.keys(values) as FieldKey[]).forEach((key) => {
    const value = values[key]?.trim();
    payload[key] = value ? value : null;
  });
  return payload;
}

export function createBillingProfileSchema(t: (key: string) => string) {
  const shape = {} as Record<FieldKey, zod.ZodString>;
  (Object.keys(MAX_LENGTH) as FieldKey[]).forEach((key) => {
    shape[key] = zod
      .string()
      .trim()
      .max(MAX_LENGTH[key], {
        message: t('BILLING_PROFILE_MAX_LENGTH').replace('{MAX}', String(MAX_LENGTH[key])),
      });
  });

  return zod.object(shape).superRefine((values, ctx) => {
    BILLING_PROFILE_REQUIRED_FIELDS.forEach((key) => {
      if (!values[key]) {
        ctx.addIssue({ code: 'custom', path: [key], message: t('BILLING_PROFILE_REQUIRED') });
      }
    });

    if (
      values.name &&
      (!ARABIC_NAME_PATTERN.test(values.name) || !ARABIC_LETTER_PATTERN.test(values.name))
    ) {
      ctx.addIssue({ code: 'custom', path: ['name'], message: t('BILLING_PROFILE_NAME_AR_INVALID') });
    }

    if (values.mobile && !isValidPhoneNumber(values.mobile)) {
      ctx.addIssue({ code: 'custom', path: ['mobile'], message: t('BILLING_PROFILE_MOBILE_INVALID') });
    }

    if (values.countryCode === 'SA') {
      (Object.keys(SA_FORMATS) as FieldKey[]).forEach((key) => {
        const rule = SA_FORMATS[key];
        if (rule && values[key] && !rule.pattern.test(values[key])) {
          ctx.addIssue({ code: 'custom', path: [key], message: t(rule.message) });
        }
      });
    }
  });
}
